// 모바일용 공장 관리 모달
import React, { useState } from "react";
import {
  createFactory,
  updateFactory,
  deleteFactory,
} from "../../api/users/companies";
import { Factory, Plus, MapPin, X } from "lucide-react";

const MobileFactoryModal = ({ company, factoryList, onClose, reloadFactoryList }) => {
  const [factoryNew, setFactoryNew] = useState({
    id: null,
    name: "",
    address: "",
    detail_address: "",
    extra_address: "",
  });
  const [showForm, setShowForm] = useState(false);

  const resetForm = () => {
    setFactoryNew({
      id: null,
      name: "",
      address: "",
      detail_address: "",
      extra_address: "",
    });
    setShowForm(false);
  };

  const addOrUpdateFactory = async () => {
    if (!company?.id) return;
    if (!factoryNew.name.trim()) {
      alert("공장명을 입력하세요.");
      return;
    }
    try {
      if (factoryNew.id) {
        // 수정
        await updateFactory(factoryNew.id, {
          company: company.id,
          name: factoryNew.name,
          address: factoryNew.address || "",
          detail_address: factoryNew.detail_address || "",
          extra_address: factoryNew.extra_address || "",
        });
        alert("공장이 수정되었습니다.");
      } else {
        // 추가
        await createFactory({
          company: company.id,
          name: factoryNew.name,
          address: factoryNew.address || "",
          detail_address: factoryNew.detail_address || "",
          extra_address: factoryNew.extra_address || "",
        });
        alert("공장이 추가되었습니다.");
      }
      resetForm();
      await reloadFactoryList();
    } catch (e) {
      console.error("공장 저장 실패:", e?.response?.data || e);
      alert("공장 저장 실패\n" + JSON.stringify(e?.response?.data || {}, null, 2));
    }
  };

  const removeFactory = async (id) => {
    if (!window.confirm("공장을 삭제하시겠습니까?")) return;
    try {
      await deleteFactory(id);
      await reloadFactoryList();
      alert("공장이 삭제되었습니다.");
    } catch (e) {
      console.error("공장 삭제 실패:", e);
      alert("공장 삭제 실패");
    }
  };

  const startEdit = (factory) => {
    setFactoryNew({ ...factory });
    setShowForm(true);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
      <div className="bg-white w-full max-w-md rounded-t-2xl max-h-[90vh] overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Factory className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="text-base font-semibold">공장 관리</h3>
              <p className="text-xs text-gray-500">{company?.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-60px)] space-y-4">
          {/* Add/Edit Form */}
          {showForm ? (
            <div className="section-card p-4 space-y-3 animate-fade-in-soft">
              <p className="text-sm font-semibold">
                {factoryNew.id ? "공장 정보 수정" : "새 공장 추가"}
              </p>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">공장명 *</label>
                <input
                  type="text"
                  placeholder="공장명"
                  value={factoryNew.name}
                  onChange={(e) => setFactoryNew((p) => ({ ...p, name: e.target.value }))}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">주소</label>
                <input
                  type="text"
                  placeholder="도로명/지번 주소"
                  value={factoryNew.address}
                  onChange={(e) => setFactoryNew((p) => ({ ...p, address: e.target.value }))}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">상세 주소</label>
                <input
                  type="text"
                  placeholder="층/호 등"
                  value={factoryNew.detail_address}
                  onChange={(e) => setFactoryNew((p) => ({ ...p, detail_address: e.target.value }))}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">참고 주소</label>
                <input
                  type="text"
                  placeholder="동/리 등"
                  value={factoryNew.extra_address}
                  onChange={(e) => setFactoryNew((p) => ({ ...p, extra_address: e.target.value }))}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button onClick={resetForm} className="btn-cancel flex-1">
                  취소
                </button>
                <button onClick={addOrUpdateFactory} className={factoryNew.id ? "btn-save flex-1" : "btn-create flex-1"}>
                  {factoryNew.id ? "저장" : "추가"}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowForm(true)}
              className="btn-create w-full flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              공장 추가
            </button>
          )}

          {/* Factory List */}
          <div className="space-y-2">
            <p className="text-sm font-semibold">등록된 공장 ({factoryList.length})</p>

            {factoryList.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-500">
                <Factory className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                등록된 공장이 없습니다.
              </div>
            ) : (
              factoryList.map((f) => (
                <div key={f.id} className="section-card p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{f.name}</p>
                      {f.address && (
                        <div className="flex items-start gap-1 mt-1">
                          <MapPin className="w-3 h-3 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="text-xs text-gray-600">
                            {f.address}
                            {f.detail_address && ` ${f.detail_address}`}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => startEdit(f)}
                        className="btn-edit-sm text-xs"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => removeFactory(f.id)}
                        className="btn-delete-sm text-xs"
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileFactoryModal;
